// "Block this number": the add form in a dialog, opened from a call in the Calls ledger or detail, from a
// contact's number, or from Add number on the Blocked and do not call page. It checks the list first:
// a number that's already there opens as its entry, and saving updates it instead of failing.
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Ban } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { type BlocklistClient, type BlocklistCreate, type BlocklistEntry, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { describeError } from '../../lib/errorCopy.ts';
import { formatAbsolute, formatPhone } from '../../lib/format.ts';
import { type DraftErrors, type EntryDraft, EntryFields, FIELD_ORDER, draftFromEntry, draftNumber, emptyDraft, numberForField, validateDraft } from './EntryForm.tsx';
import { listsText, readEntry, readLookup } from './model.ts';
import { BlocklistInlineStyles } from './InlineStyles.tsx';

export type SavedChange = 'added' | 'updated';

/** Plain copy for the errors these forms can meet; the server's own sentence stays as the detail. */
export function blocklistErrorText(error: unknown, fallback: string): string {
  if (isApiRequestError(error)) {
    if (error.code === 'blocklist_exists') return 'This number is already on the list.';
    if (error.code === 'permission_denied') return 'Your role can view the blocked list but not change it. Ask an admin if you need to.';
    if (error.code === 'not_found') return 'This entry isn’t on the list any more. Close this and reload the list.';
    if (error.code === 'invalid_input') return `Some details aren’t valid. ${error.message}`;
  }
  const view = describeError(error, fallback);
  return [view.title, view.detail, view.requestId ? `Reference ${view.requestId}.` : ''].filter(Boolean).join(' ');
}

export type SaveOutcome =
  | { kind: 'saved'; entry: BlocklistEntry | null; change: SavedChange }
  /** Someone added the number while the form was open: here's their entry; nothing was changed. */
  | { kind: 'exists'; entry: BlocklistEntry }
  | { kind: 'error'; message: string };

/**
 * Creates the entry, or updates `existing`. A create that meets 409 `blocklist_exists` fetches the entry
 * that won and hands it back instead of overwriting it, so the person reviews it before saving again.
 */
export async function saveEntry(client: Pick<BlocklistClient, 'create' | 'update' | 'lookup'>, existing: BlocklistEntry | null, body: BlocklistCreate): Promise<SaveOutcome> {
  try {
    if (existing) {
      const saved = readEntry(await client.update(existing.id, { lists: body.lists, reason: body.reason, note: body.note ?? '', expires_at: body.expires_at ?? null }));
      return { kind: 'saved', entry: saved, change: 'updated' };
    }
    return { kind: 'saved', entry: readEntry(await client.create(body)), change: 'added' };
  } catch (error) {
    if (!existing && isApiRequestError(error) && error.code === 'blocklist_exists') {
      try {
        const found = readLookup(await client.lookup(body.e164));
        return found?.entry ? { kind: 'exists', entry: found.entry } : { kind: 'error', message: blocklistErrorText(error, 'Couldn’t block this number') };
      } catch (lookupError) {
        return { kind: 'error', message: blocklistErrorText(lookupError, 'Couldn’t check the list') };
      }
    }
    return { kind: 'error', message: blocklistErrorText(error, existing ? 'Couldn’t update this entry' : 'Couldn’t block this number') };
  }
}

export function BlockNumberDialog({ open, number, noteSeed, client, onClose, onSaved }: {
  open: boolean;
  /** E.164 to prefill, or null for an empty number field. */
  number: string | null;
  /** Prefilled note, for example which call the block came from. */
  noteSeed?: string | undefined;
  client: BlocklistClient;
  onClose: () => void;
  onSaved?: ((entry: BlocklistEntry | null, change: SavedChange) => void) | undefined;
}) {
  const uid = useId();
  const [mode, setMode] = useState<SavedChange>('added');
  return (
    <Dialog open={open} title={mode === 'updated' ? 'Update blocked number' : 'Block this number'} onClose={onClose} labelledBy={`bl-dialog-${uid}`}>
      {open ? (
        <BlockForm
          key={number ?? ''}
          idPrefix={`bl-${uid}`}
          initialNumber={number}
          noteSeed={noteSeed ?? ''}
          client={client}
          onMode={setMode}
          onCancel={onClose}
          onDone={(entry, change) => { onSaved?.(entry, change); onClose(); }}
        />
      ) : null}
    </Dialog>
  );
}

type Lookup = { state: 'idle' | 'checking' | 'done' | 'error'; e164: string | null; entry: BlocklistEntry | null };

function BlockForm({ idPrefix, initialNumber, noteSeed, client, onMode, onCancel, onDone }: {
  idPrefix: string;
  initialNumber: string | null;
  noteSeed: string;
  client: BlocklistClient;
  onMode: (mode: SavedChange) => void;
  onCancel: () => void;
  onDone: (entry: BlocklistEntry | null, change: SavedChange) => void;
}) {
  const { announce } = useAnnouncer();
  const [draft, setDraft] = useState<EntryDraft>(() => emptyDraft(initialNumber ? numberForField(initialNumber) : '', noteSeed));
  const [errors, setErrors] = useState<DraftErrors>({});
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle', e164: null, entry: null });
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [raced, setRaced] = useState(false);
  const touched = useRef(false);
  const phone = draftNumber(draft.number);
  const e164 = phone.ok ? phone.e164 : null;
  const existing = lookup.e164 === e164 ? lookup.entry : null;

  // Focus the first thing to fill in: the reason when the number came with the dialog. Deferred so it
  // lands after showModal(), which runs in the parent's effect and focuses the first control itself.
  useEffect(() => {
    const t = setTimeout(() => document.getElementById(`${idPrefix}-${initialNumber ? 'reason' : 'number'}`)?.focus(), 0);
    return () => clearTimeout(t);
  }, [idPrefix, initialNumber]);

  useEffect(() => {
    if (!e164) { setLookup({ state: 'idle', e164: null, entry: null }); return; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLookup({ state: 'checking', e164, entry: null });
      client.lookup(e164, controller.signal).then(
        body => {
          const result = readLookup(body);
          const entry = result?.blocked ? result.entry : null;
          setLookup({ state: result ? 'done' : 'error', e164, entry });
          if (entry && !touched.current) setDraft(d => ({ ...draftFromEntry(entry), number: d.number }));
        },
        error => { if (!isAbortError(error)) setLookup({ state: 'error', e164, entry: null }); },
      );
    }, initialNumber && e164 === initialNumber ? 0 : 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [client, e164, initialNumber]);

  useEffect(() => { onMode(existing ? 'updated' : 'added'); }, [existing, onMode]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const result = validateDraft(draft);
    if (!result.ok) {
      setErrors(result.errors);
      const first = FIELD_ORDER.find(f => result.errors[f]);
      if (first) document.getElementById(`${idPrefix}-${first}`)?.focus();
      return;
    }
    setErrors({});
    setServerError(null);
    setBusy(true);
    const outcome = await saveEntry(client, existing, result.body);
    setBusy(false);
    if (outcome.kind === 'saved') {
      announce(outcome.change === 'added' ? `Blocked ${formatPhone(result.body.e164)}.` : `Updated the entry for ${formatPhone(result.body.e164)}.`);
      onDone(outcome.entry, outcome.change);
    } else if (outcome.kind === 'exists') {
      setLookup({ state: 'done', e164: result.body.e164, entry: outcome.entry });
      setRaced(true);
    } else {
      setServerError(outcome.message);
    }
  };

  const status = lookup.e164 !== e164 || lookup.state === 'idle' ? null
    : lookup.state === 'checking' ? 'Checking the list…'
    : lookup.state === 'error' ? 'Couldn’t check whether it’s already listed. You can still save.'
    : existing ? 'Already on the list.' : 'Not on the list yet.';

  return (
    <form className="detail" onSubmit={submit} noValidate aria-describedby={existing ? `${idPrefix}-existing` : undefined}>
      <BlocklistInlineStyles />
      {existing ? (
        <p id={`${idPrefix}-existing`} className="notice notice-info" role="status">
          {raced ? 'Someone added this number while you were filling this in. ' : ''}
          On the list since {formatAbsolute(existing.added_at)}, blocked for {listsText(existing.lists)}. Saving updates this entry.
        </p>
      ) : null}
      {serverError ? <p className="form-error" role="alert">{serverError}</p> : null}
      <EntryFields
        draft={draft}
        errors={errors}
        idPrefix={idPrefix}
        onChange={patch => {
          if (!('number' in patch)) touched.current = true;
          setDraft(d => ({ ...d, ...patch }));
        }}
        numberHint={e164 ? `Saved as ${e164}.${status ? ` ${status}` : ''}` : null}
      />
      <div className="form-actions">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          {busy ? 'Saving…' : existing ? 'Update entry' : <><Icon icon={Ban} /> Block number</>}
        </button>
      </div>
    </form>
  );
}

/** A button that opens the dialog for one number. `compact` draws the icon-only ledger form. */
export function BlockNumberButton({ e164, client, noteSeed, blocked = false, onSaved, className = 'btn btn-small' }: {
  e164: string;
  client: BlocklistClient;
  noteSeed?: string | undefined;
  /** Already on the list: the button opens the entry for editing. */
  blocked?: boolean;
  onSaved?: ((entry: BlocklistEntry | null, change: SavedChange) => void) | undefined;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)} aria-haspopup="dialog">
        <Icon icon={Ban} /> {blocked ? 'Edit block' : 'Block this number'}
      </button>
      <BlockNumberDialog open={open} number={e164} noteSeed={noteSeed} client={client} onClose={() => setOpen(false)} onSaved={onSaved} />
    </>
  );
}
